import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { LayoutDashboard, Dumbbell, MessageSquare, Settings, Sun, Moon, LogOut, Shield, Bug, MoreVertical, BarChart3, Trophy, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useState } from 'react';
import { ReportIssueModal } from '@/components/ReportIssueModal';
import { ThemeSelector } from '@/components/ThemeSelector';

const NAV_LINKS = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Home' },
  { to: '/workout', icon: Dumbbell, label: 'Program' },
  { to: '/analytics', icon: BarChart3, label: 'Progress' },
  { to: '/progression', icon: TrendingUp, label: 'Level' },
  { to: '/chat', icon: MessageSquare, label: 'Coach' },
  { to: '/achievements', icon: Trophy, label: 'Awards' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

export function NavBar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [showReportModal, setShowReportModal] = useState(false);

  return (
    <nav
      className="sticky top-0 z-50 backdrop-blur-xl border-b"
      style={{ background: 'rgba(10,10,10,0.7)', borderColor: 'rgba(255,255,255,0.05)', paddingTop: 'env(safe-area-inset-top, 0px)' }}
      data-testid="main-navbar"
    >
      <div className="flex items-center justify-between h-16 px-4 max-w-7xl mx-auto">
        {/* Brand */}
        <Link to="/dashboard" className="shrink-0" data-testid="nav-logo">
          <img src="/bodymax-logo.png" alt="BODYMAX.FIT" className="h-12 w-auto" />
        </Link>

        {/* Desktop nav links — hidden on mobile */}
        <div className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map(link => (
            <Link key={link.to} to={link.to}>
              <Button
                asChild
                variant={location.pathname === link.to ? 'secondary' : 'ghost'}
                className="rounded-full gap-2 h-9 text-sm"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                <span>
                  <link.icon size={16} />
                  {link.label}
                </span>
              </Button>
            </Link>
          ))}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-1">
          <ThemeSelector className="w-9 h-9 hover:bg-white/5" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-9 h-9 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground active:scale-95 transition-all"
                data-testid="nav-user-menu"
              >
                <MoreVertical size={18} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
              <DropdownMenuItem className="text-muted-foreground text-xs cursor-default">
                {user?.email}
              </DropdownMenuItem>
              {user?.is_dev && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate('/admin')}
                    className="text-accent"
                    data-testid="nav-admin"
                  >
                    <Shield size={14} className="mr-2" /> Admin
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowReportModal(true)}
                data-testid="nav-report-issue"
              >
                <Bug size={14} className="mr-2" /> Report Issue
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => { logout(); navigate('/'); }}
                data-testid="nav-logout"
              >
                <LogOut size={14} className="mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <ReportIssueModal open={showReportModal} onOpenChange={setShowReportModal} />
    </nav>
  );
}
